/**
 * Query hooks for Trips
 * 
 * These hooks encapsulate all trip-related data fetching
 * with automatic caching, deduplication, and refetching.
 */

import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query/client';
import { BACKEND_URL } from "../../config";
import { useAuth } from "@/contexts/AuthContext";
/**
 * Fetch public trips (for Comunidade page)
 */
/**
 * Fetch public trips (for Comunidade / Passeios pages).
 *
 * @param {object} [options]
 * @param {number} [options.limit] - return only the first N trips (homepage teaser).
 * @param {boolean} [options.enabled=true] - gate the query (lazy fetch the full list).
 */
export function usePublicTrips(options = {}) {
  const { limit, enabled = true } = options;
  return useQuery({
    queryKey: limit ? [...queryKeys.trips.public(), { limit }] : queryKeys.trips.public(),
    queryFn: async () => {
      const res = await api.get(`/api/trips/public${limit ? `?limit=${limit}` : ""}`);
      return res.data;
    },
    enabled,
    staleTime: 60 * 1000, // 1 minute - public trips change infrequently
  });
}

/**
 * Fetch a single trip by ID
 * Supports both authenticated and public endpoints with automatic fallback
 */
export function useTrip(tripId, { isAuthenticated = false, ...options } = {}) {
  return useQuery({
    queryKey: queryKeys.trips.detail(tripId),
    queryFn: async () => {
      if (isAuthenticated) {
        try {
          const res = await api.get(`/api/trips/${tripId}`);
          return res.data;
        } catch (authError) {
          // If 404 or forbidden, try public endpoint
          if (authError.response?.status === 404 || authError.response?.status === 403) {
            const res = await api.get(`/api/trips/public/${tripId}`);
            return res.data;
          }
          throw authError;
        }
      } else {
        // Not authenticated - use public endpoint
        const res = await api.get(`/api/trips/public/${tripId}`);
        return res.data;
      }
    },
    enabled: !!tripId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Fetch trip owner/creator info
 */
export function useTripOwner(userId, options = {}) {
  return useQuery({
    queryKey: ['users', 'profile', userId],
    queryFn: async () => {
      const res = await api.get(`/api/profile/${userId}`);
      return res.data;
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Check if trip is pinned by current user
 */
export function useTripPinStatus(tripId) {
  return useQuery({
    queryKey: [...queryKeys.trips.detail(tripId), 'pin-status'],
    queryFn: async () => {
      const res = await api.get(`/api/trips/${tripId}/pin-status`);
      return res.data;
    },
    enabled: !!tripId,
    staleTime: 30 * 1000,
  });
}

/**
 * Check if trip is liked by current user  
 */
export function useTripLikeStatus(tripId) {
  return useQuery({
    queryKey: [...queryKeys.trips.detail(tripId), 'like-status'],
    queryFn: async () => {
      const res = await api.get(`/api/trips/${tripId}/like-status`);
      return res.data;
    },
    enabled: !!tripId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch user's own trips (personal trips only)
 */
export function useMyTrips({ ownerType = null, sortBy = 'date' } = {}) {
  return useQuery({
    queryKey: [...queryKeys.trips.myTrips(), ownerType, sortBy],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (ownerType) params.append('owner_type', ownerType);
      
      const url = params.toString() 
        ? `/api/trips?${params.toString()}`
        : "/api/trips";
      
      const res = await api.get(url);
      
      // Sort by date if requested
      if (sortBy === 'date') {
        return [...res.data].sort((a, b) => {
          const dateA = new Date(a.trip_date || a.created_at);
          const dateB = new Date(b.trip_date || b.created_at);
          return dateB - dateA;
        });
      }
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch pinned trip IDs for current user
 */
export function usePinnedTripIds() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: queryKeys.trips.pinnedIds(),
    queryFn: async () => {
      const res = await api.get("/api/trips/pinned/ids");
      return new Set(res.data);
    },
    staleTime: 60 * 1000,
    enabled: isAuthenticated,
  });
}

/**
 * Fetch liked trip IDs for current user
 */
export function useLikedTripIds() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: queryKeys.trips.likedIds(),
    queryFn: async () => {
      const res = await api.get("/api/trips/liked/ids");
      return new Set(res.data);
    },
    staleTime: 60 * 1000,
    enabled: isAuthenticated,
  });
}


/**
 * Fetch trips for a specific club
 */
export function useClubTrips(clubId, { sortBy = 'date' } = {}) {
  return useQuery({
    queryKey: ['club-trips', clubId, sortBy],
    queryFn: async () => {
      const res = await api.get("/api/trips", {
        params: { club_ids: clubId, owner_type: 'club' }
      });
      
      // Sort by date if requested
      if (sortBy === 'date') {
        return [...res.data].sort((a, b) => {
          const dateA = new Date(a.trip_date || a.created_at);
          const dateB = new Date(b.trip_date || b.created_at);
          return dateB - dateA;
        });
      }
      return res.data;
    },
    enabled: !!clubId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch trips for multiple clubs at once
 */
export function useMultiClubTrips(clubIds, { enabled = true } = {}) {
  return useQuery({
    queryKey: ['club-trips', 'multi', clubIds],
    queryFn: async () => {
      if (!clubIds || clubIds.length === 0) return {};
      
      const results = await Promise.all(
        clubIds.map(async (clubId) => {
          try {
            const res = await api.get("/api/trips", {
              params: { club_ids: clubId, owner_type: 'club' }
            });
            return { clubId, trips: res.data || [] };
          } catch {
            return { clubId, trips: [] };
          }
        })
      );
      
      // Return as object keyed by clubId
      const tripsByClub = {};
      results.forEach(({ clubId, trips }) => {
        tripsByClub[clubId] = trips;
      });
      return tripsByClub;
    },
    enabled: enabled && !!clubIds && clubIds.length > 0,
    staleTime: 30 * 1000,
  });
}

